import { useCallback, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  getIsShopPrivate,
  getIsLockerShop,
  getStoreAccessibilityEnabled,
  getIsBundleShop,
} from "@selectors/store";
import { getCartItemsCount } from "@store/selectors/cart";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import useLogout from "@hooks/use-logout";
import useShowNewHeader from "@hooks/use-show-new-header";
import { useWorkspaceContext } from "@contexts/workspace-context";
import { useStoreContext } from "@components/store/provider";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import { useFlags } from "@stadium/services/launch-darkly";

import { CurrentUserContext } from "@contexts/current-user-provider";

import Avatar from "@stadium/shared/header/avatar";

import { BYSTADIUM_MARKETING_URL } from "@stadium/helpers/src/app-routes";
import { MY_WORKSPACE_SLUG } from "@stadium/constants";

import ResponsiveProfileDropdown from "@stadium/shared/src/components/header/profile-dropdown/responsive";
import EmptyWorkspaceCartModal from "@components/modals/empty-workspace-cart";

import { workspaceSidebarFlag } from "@stadium/services/launch-darkly/constants";
import { getStadiumRouteWithWorkspaceSlug } from "@stadium/shared/src/constants/routes";

import { getUrlWithWorkspaceSlug } from "@stadium/helpers/routes";
import {
  getCatalogRedirectUrl,
  getLegacyMySpaceRedirectUrl,
  shouldRedirectToCatalog,
} from "@helpers/workspace";
import { ANALYTICS_EVENT_ACTION_SWITCHED } from "@stadium/services/analytics/constants/default";
import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";
import { decodeMeta } from "@helpers/route";

import styles from "./main.module.scss";

const ProfileDropdown = () => {
  const { query } = useRouter();
  const { hostname } = decodeMeta(query.meta);
  const { currentUserData } = useContext(CurrentUserContext);
  const { name, firstName, lastName, email, defaultWorkspace, fullName, avatarUrl } =
    currentUserData;
  const { isNewRoute, isNewSwagmagicHost, countryCode, lang, storeCode, storeNumber } =
    useStoreContext();
  const { [workspaceSidebarFlag]: workspaceSidebarFlagEnabled } = useFlags();

  const [cartModalData, setCartModalData] = useState(null);

  const isKitShop = useSelector(getIsBundleShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const itemsCount = useSelector(getCartItemsCount);

  const isShopPrivate = useSelector(getIsShopPrivate);
  const accessibilityEnabled = useSelector(getStoreAccessibilityEnabled);
  const { workspaceSlug, permissions, workspaces, setSelectedWorkspaceSlug } =
    useWorkspaceContext();
  const showNewHeader = useShowNewHeader();

  const isPrivateShopAndNotLockerShop = isShopPrivate && !isLockerShop;

  const { logoutAction } = useLogout(isPrivateShopAndNotLockerShop ? BYSTADIUM_MARKETING_URL : "");

  const renderHead = useCallback(() => {
    if (isNewSwagmagicHost) return <div>Profile</div>;
    if (workspaceSidebarFlagEnabled) return null;
    return (
      <div className={styles.profileTabContainer}>
        <div className={styles.profileTab}>
          <Avatar user={currentUserData} />
          <div>{name || email}</div>
        </div>
      </div>
    );
  }, [name, email]);

  const withAnalytics = useWithAnalytics();

  const trackWorkspaceSwitch = useCallback(
    (selectedWorkspaceSlug) => {
      withAnalytics(undefined, {
        track: true,
        target: HEADER_ANALYTICS_TARGET.WORKSPACE,
        action: ANALYTICS_EVENT_ACTION_SWITCHED,
        eventProperties: { workspace_slug: selectedWorkspaceSlug },
      })();
    },
    [withAnalytics],
  );

  const onCartEmpty = ({ selectedWorkspaceSlug, currentPath }) => {
    trackWorkspaceSwitch(selectedWorkspaceSlug);
    setSelectedWorkspaceSlug(selectedWorkspaceSlug);

    //TODO - New Shop Route Migration - Update this condition on store new route enable basis.
    if (isPrivateShopAndNotLockerShop) {
      window.location.href = getStadiumRouteWithWorkspaceSlug({
        workspaceSlug: selectedWorkspaceSlug,
      });
      return;
    }

    const redirectToCatalog = shouldRedirectToCatalog({
      currentPath,
      hostname,
      catchAll: query?.catchAll,
    });
    if (redirectToCatalog) {
      window.location.href = getCatalogRedirectUrl({
        countryCode,
        lang,
        storeCode,
        storeNumber,
        selectedWorkspaceSlug,
        currentPath,
      });
      return;
    }
    const isNewWorkspacePath = currentPath.includes("/w/") || currentPath.includes("/s/");
    if (isNewWorkspacePath) {
      const updatedWorkspaceUrl = getUrlWithWorkspaceSlug({
        url: currentPath,
        fromWorkspaceSlug: workspaceSlug,
        toWorkspaceSlug: selectedWorkspaceSlug,
      });
      if (updatedWorkspaceUrl !== currentPath) {
        globalThis.location.href = updatedWorkspaceUrl;
        return;
      }
    }
    // Legacy path: /[countryCode]/[lang]/stores/[slug]/[storeNumber]/...
    // When workspace is my-space but path has no -- in slug, add --selectedWorkspaceSlug to slug
    if (workspaceSlug === MY_WORKSPACE_SLUG && !currentPath.includes(MY_WORKSPACE_SLUG)) {
      const redirectUrl = getLegacyMySpaceRedirectUrl({
        currentPath,
        isNewRoute,
        selectedWorkspaceSlug,
      });
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
    }
    // Legacy: slug--workspace format (e.g. parry-moment--140603-c531de)
    window.location.href = currentPath.replace(`--${workspaceSlug}`, `--${selectedWorkspaceSlug}`);
  };

  const onWorkspaceSelection = (selectedWorkspaceSlug, currentPath) => {
    if (isKitShop || itemsCount === 0) {
      return onCartEmpty({ selectedWorkspaceSlug, currentPath });
    }
    setCartModalData({
      selectedWorkspaceSlug,
      currentPath,
    });
  };

  const renderLabel = useCallback(() => {
    if (isNewSwagmagicHost) {
      return (
        <>
          <Avatar user={currentUserData} showAvatarInResponsive />
          <p className={styles.categoryText}>Profile</p>
        </>
      );
    }
    if (showNewHeader && !workspaceSidebarFlagEnabled) {
      return (
        <div className={styles.profileTabContainer}>
          <div className={styles.profileTab}>
            <Avatar user={currentUserData} />
            <div className={styles.profileTabCenterContainer}>My Account</div>
          </div>
        </div>
      );
    }
    return null;
  }, [isNewSwagmagicHost, showNewHeader, workspaceSidebarFlagEnabled, currentUserData]);

  const profileData = useMemo(
    () => ({
      name,
      firstName,
      lastName,
      email,
      headLabel: renderHead(),
      workspaces,
      defaultWorkspace,
      activeWorkspaceSlug: workspaceSlug,
      onWorkspaceSelection,
      canViewShops: permissions.canViewShops,
      fullName,
      avatarUrl,
      label: renderLabel(),
    }),
    [
      email,
      name,
      renderHead,
      workspaces,
      defaultWorkspace,
      isPrivateShopAndNotLockerShop,
      workspaceSlug,
      permissions.canViewShops,
      fullName,
      avatarUrl,
      renderLabel,
    ],
  );

  return (
    <>
      {cartModalData && (
        <EmptyWorkspaceCartModal
          closeModal={() => setCartModalData(null)}
          onSuccess={() => onCartEmpty(cartModalData)}
        />
      )}
      <ResponsiveProfileDropdown
        styles={styles}
        logout={logoutAction}
        profileData={profileData}
        accessibilityEnabled={accessibilityEnabled}
        isNewSwagmagicHost={isNewSwagmagicHost}
      />
    </>
  );
};

export default ProfileDropdown;
